import { Component, OnDestroy, OnInit } from '@angular/core';
import { ViewChild } from '@angular/core';
import { SitesDataService } from "../data-storage/sites-data.service";
import { SiteDto } from "../../../dtos/siteDto";
import { Observable, Subscription } from "rxjs";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { Router } from '@angular/router';
import { map, switchMap } from "rxjs/operators";
import { skipWhile } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";
import { HotSpot } from "../../../dtos/hotSpot";
import { PanoramaImages } from "../../../dtos/panoramaImages";
import { MonumentsDataService } from "../data-storage/monuments-data.service";
import { MonumentDto } from "../../../dtos/monumentDto";
import { GlobalDataService } from "../../../shared-services/global-data.service";
import { UserDataService } from '../data-storage/user-data.service';
import { SubscriptionPacketsService } from '../services/subscription-packets.service';
import { PanoramaContainerComponent } from '../../../shared/mobileLayout/panorama-container/panorama-container.component';
import { combineLatest } from 'rxjs';
import { Renderer2 } from '@angular/core';

@Component({
    selector: 'app-site-detail',
    templateUrl: './site-detail.component.html',
    styleUrls: ['./site-detail.component.scss']
})
export class SiteDetailComponent implements OnInit, OnDestroy {

    constructor(private sitesDataService: SitesDataService, private route: ActivatedRoute,
                private monumentsDataService: MonumentsDataService,
                public globalDataService: GlobalDataService,
                public translate: TranslateService,
                private router: Router,
                private userDataService: UserDataService,
                private subscriptionPacketsService: SubscriptionPacketsService) {
    }

    isLoginOpen = false;
    isMapOpen = false;
    isPacketOpen = false;
    isMonumentOpen = false;
    flowLoading = false;
    currentSite: SiteDto;
    sitePanoramicHotspots: HotSpot[];
    selectedPanorama: PanoramaImages;
    monuments$: Observable<MonumentDto[]>;
    monumentsMarkers$: Observable<any>;
    panoramicCenterRotation: number;
    selectedMonument: MonumentDto;
    private subscriptions: Subscription[] = [];

    @ViewChild(PanoramaContainerComponent)
    panoramaContainer: PanoramaContainerComponent;

    ngOnInit(): void {
        const subscriptionParams =
            this.route.paramMap.pipe(
                switchMap(
                    (params: ParamMap) => this.sitesDataService.sites.pipe(
                        map((sites: SiteDto[]) => sites.filter((site: SiteDto) => site.seotitle === params.get("seotitle"))[0])
                    )
                )
            ).subscribe(this.populateSite.bind(this));


        this.subscriptions.push(subscriptionParams);

        const subscriptionTranslate =
            this.translate.onLangChange
                .subscribe(() => {
                    if (this.sitePanoramicHotspots) {
                        this.sitePanoramicHotspots = [...this.selectedPanorama.panoramaHotSpots];
                    }
                })

        this.subscriptions.push(subscriptionTranslate);

    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        })
    }

    toggleMap() {
        this.isMapOpen = !this.isMapOpen;
    }

    toggleMonument() {
        this.isMonumentOpen = !this.isMonumentOpen;
    }

    rotationHandler(event) {
        this.panoramicCenterRotation = -event;
    }

    handleConfigurationChange(event) {
        const hotSpot: HotSpot = event.hotSpot;
        if (hotSpot && hotSpot.hotSpotType === 'NAVIGATION') {
            if (hotSpot.panoramaComponent === 'PANORAMA') {
                this.selectedPanorama = null;
                this.selectedPanorama = this.currentSite.panoramaImages.find((panorama) => panorama.id === hotSpot.componentID);
                setTimeout(() => {
                    this.sitePanoramicHotspots = [...this.selectedPanorama.panoramaHotSpots];
                })
            }
            if (hotSpot.panoramaComponent === 'MONUMENT') {
                const monument = this.monumentsDataService.getMonumentData().find((monument: MonumentDto) => monument.id === hotSpot.componentID);
                this.openMonument(monument);
            }
        }
    }

    isSitePurchased() {
        return this.subscriptionPacketsService.isSitePurchased(this.currentSite) || this.currentSite.free;
    }

    openMonument(monument) {
        this.flowLoading = true;
        this.selectedMonument = null;
        const subscription = combineLatest([this.sitesDataService.getSiteById(monument.siteId), this.subscriptionPacketsService.checkUserPacket(monument.seotitle)])
            .subscribe((value) => {
                this.flowLoading = false;
                if (value[1] || value[0].free) {
                    this.isMonumentOpen = true;
                    this.selectedMonument = monument;
                } else {
                    this.noValidPacketHandle();
                }
                subscription.unsubscribe();
            });
    }

    clickPoint(event) {
        this.monuments$
            .pipe(
                map((monuments) => monuments.find((monument) => monument.id === event.id))
            )
            .subscribe(this.openMonument.bind(this))
            .unsubscribe();

    }

    changePeriod(natural = false) {
        this.selectedPanorama = this.currentSite.panoramaImages.find((panorama) => panorama.natural === natural && panorama.mobile != natural );
        setTimeout(() => {
            this.sitePanoramicHotspots = [...this.selectedPanorama.panoramaHotSpots];
        })
    }

    private populateSite(selectedSite: SiteDto) {
        if (selectedSite) {
            this.currentSite = selectedSite;
            this.monuments$ = this.monumentsDataService.getMonumentBySiteId(this.currentSite.id)
                .pipe(
                    map((monuments)=>{
                        return monuments.sort((a, b) => {
                            const nameA = this.globalDataService.getObjectTranslation(a['monumentTranslations'], 'name');
                            const nameB = this.globalDataService.getObjectTranslation(b['monumentTranslations'], 'name');
                            if (nameA < nameB) {
                                return -1;
                            }
                            return nameA > nameB ? 1 : 0
                        });
                    })
                );
            this.selectedPanorama = this.currentSite.panoramaImages.find((panorama) => panorama.mobile);
            this.sitePanoramicHotspots = this.selectedPanorama.panoramaHotSpots;
            this.monumentsMarkers$ = this.monuments$.pipe(
                skipWhile((monuments: MonumentDto[]) => monuments.length === 0),
                map((monuments: MonumentDto[]) =>
                    monuments.map((monument) => {
                        const markerElement = document.createElement("div");
                        markerElement.style.width = "30px";
                        markerElement.style.height = "30px";
                        markerElement.style.backgroundSize = "cover";
                        markerElement.style.backgroundImage = 'url(assets/images/remotes/simplemarker.png)';
                        return {
                            marker: [monument.longitude, monument.latitude],
                            data: {
                                id: monument.id,
                                tooltipTranslations: monument.monumentTranslations
                            },
                            element: markerElement,
                            map: null
                        }
                    })
                )
            )
        }
    }

    private noValidPacketHandle() {
        if (this.userDataService.getUser().id) {
            this.isPacketOpen = true;
        } else {
            this.isLoginOpen = false;
            setTimeout(() => {
                this.isLoginOpen = true;
            })
        }
    }
}
